import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { UserPlus, Loader2, Mail, Building2, Phone, MapPin, CreditCard, Layers } from "lucide-react";

/**
 * Format a free-form string of digits as a US phone number.
 * Called on every keystroke so the field is always in `(###) ###-####` shape.
 * Non-digits are stripped first; we build the mask progressively as the
 * user types so partial values still render (e.g. "(415" or "(415) 555").
 */
export function formatUsPhone(raw) {
  const d = (raw || "").replace(/\D/g, "").slice(0, 10);
  if (d.length === 0) return "";
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME",
  "MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA",
  "RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC","PR",
];

const BLANK = {
  name: "",
  legal_name: "",
  contact_name: "",
  contact_email: "",
  phone: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "US",
  plan: "trial",
  billing_mode: "client_trial",
  seats_limit: 3,
  bank_account_limit: 2,
  send_invite_email: true,
  template_id: "",
};

export default function AddClientModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [templates, setTemplates] = useState([]);

  // Load templates once when the modal opens; pre-select the firm default.
  useEffect(() => {
    if (!open) return;
    api.get("/firm/templates").then((r) => {
      setTemplates(r.data || []);
      const def = (r.data || []).find((t) => t.is_default);
      if (def) setForm((prev) => (prev.template_id ? prev : { ...prev, template_id: def.id }));
    }).catch(() => setTemplates([]));
  }, [open]);

  const set = (field) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: v }));
  };
  const setPhone = (e) => setForm((prev) => ({ ...prev, phone: formatUsPhone(e.target.value) }));

  const close = () => {
    if (saving) return;
    setForm(BLANK);
    setErr("");
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    // Front-end guardrails that mirror the backend validators — surface
    // the error inline so the user doesn't have to send-and-fail.
    if (!/^\(\d{3}\) \d{3}-\d{4}$/.test(form.phone)) {
      setErr("Phone must be in US format (###) ###-####");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        legal_name: form.legal_name || undefined,
        address_line2: form.address_line2 || undefined,
        template_id: form.template_id || undefined,
        seats_limit: Number(form.seats_limit) || 3,
        bank_account_limit: Number(form.bank_account_limit) || 2,
      };
      const { data } = await api.post("/firm/clients", payload);
      toast.success(
        data.invite_sent
          ? `${data.tenant.name} created · invite emailed to ${form.contact_email}`
          : `${data.tenant.name} created · invite not emailed`,
      );
      if (data.invite_email_error) {
        toast.warning(`Invite email failed to send — you can resend from the client card. (${data.invite_email_error})`);
      }
      setForm(BLANK);
      onCreated?.(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // FastAPI validation errors arrive as an array of {loc,msg}; flatten.
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${(d.loc || []).slice(-1)[0]}: ${d.msg}`).join(" · ")
        : (detail || "Could not create client workspace");
      setErr(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="add-client-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <UserPlus size={16} /> Add a client workspace
          </DialogTitle>
          <DialogDescription className="text-[var(--ll-ink-soft)]">
            Spin up a new workspace for a client. You'll be auto-granted <strong>accountant</strong>
            {" "}access so you can start uploading statements and bookkeeping immediately.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5 pt-2">
          {/* Company */}
          <section>
            <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-2 flex items-center gap-1.5">
              <Building2 size={11} /> Company
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs mb-1">Company name <span className="text-[var(--ll-error)]">*</span></label>
                <input
                  required value={form.name} onChange={set("name")}
                  data-testid="client-name" placeholder="Acme Co"
                  className="w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs mb-1">Legal name <span className="text-[var(--ll-ink-muted)]">(optional)</span></label>
                <input
                  value={form.legal_name} onChange={set("legal_name")}
                  data-testid="client-legal-name" placeholder="Acme Corporation, LLC"
                  className="w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}
                />
              </div>
            </div>
          </section>

          {/* Contact */}
          <section>
            <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-2 flex items-center gap-1.5">
              <Mail size={11} /> Primary contact
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs mb-1">Contact name <span className="text-[var(--ll-error)]">*</span></label>
                <input
                  required value={form.contact_name} onChange={set("contact_name")}
                  data-testid="client-contact-name" placeholder="Jane Doe"
                  className="w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}
                />
              </div>
              <div>
                <label className="block text-xs mb-1">Contact email <span className="text-[var(--ll-error)]">*</span></label>
                <input
                  required type="email" value={form.contact_email} onChange={set("contact_email")}
                  data-testid="client-contact-email" placeholder="jane@acme.com"
                  className="w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs mb-1 flex items-center gap-1.5">
                  <Phone size={10} /> Phone (US) <span className="text-[var(--ll-error)]">*</span>
                </label>
                <input
                  required value={form.phone} onChange={setPhone}
                  data-testid="client-phone" placeholder="(415) 555-0199"
                  maxLength={14}
                  className="w-full px-3 py-2 border text-sm font-mono-fin"
                  style={{ borderColor: "var(--ll-border)" }}
                />
                <div className="text-[10px] text-[var(--ll-ink-muted)] mt-1">
                  Format: (###) ###-#### — digits auto-mask as you type
                </div>
              </div>
            </div>
          </section>

          {/* Address */}
          <section>
            <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-2 flex items-center gap-1.5">
              <MapPin size={11} /> Business address
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
              <div className="sm:col-span-6">
                <label className="block text-xs mb-1">Street address <span className="text-[var(--ll-error)]">*</span></label>
                <input
                  required value={form.address_line1} onChange={set("address_line1")}
                  data-testid="client-address1" placeholder="500 Market St"
                  className="w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}
                />
              </div>
              <div className="sm:col-span-6">
                <label className="block text-xs mb-1">Suite / unit <span className="text-[var(--ll-ink-muted)]">(optional)</span></label>
                <input
                  value={form.address_line2} onChange={set("address_line2")}
                  data-testid="client-address2" placeholder="Suite 1200"
                  className="w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}
                />
              </div>
              <div className="sm:col-span-3">
                <label className="block text-xs mb-1">City <span className="text-[var(--ll-error)]">*</span></label>
                <input
                  required value={form.city} onChange={set("city")}
                  data-testid="client-city" placeholder="San Francisco"
                  className="w-full px-3 py-2 border text-sm" style={{ borderColor: "var(--ll-border)" }}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs mb-1">State <span className="text-[var(--ll-error)]">*</span></label>
                <select
                  required value={form.state} onChange={set("state")}
                  data-testid="client-state"
                  className="w-full px-3 py-2 border text-sm bg-white" style={{ borderColor: "var(--ll-border)" }}
                >
                  <option value="">Select…</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="sm:col-span-1">
                <label className="block text-xs mb-1">ZIP <span className="text-[var(--ll-error)]">*</span></label>
                <input
                  required value={form.postal_code} onChange={set("postal_code")}
                  data-testid="client-postal" placeholder="94103" maxLength={10}
                  className="w-full px-3 py-2 border text-sm font-mono-fin"
                  style={{ borderColor: "var(--ll-border)" }}
                />
              </div>
            </div>
          </section>

          {/* Billing + limits */}
          <section>
            <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-2 flex items-center gap-1.5">
              <CreditCard size={11} /> Billing & limits
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Billing mode */}
                <button
                  type="button"
                  onClick={() => set("billing_mode")("firm_pays")}
                  data-testid="billing-firm_pays"
                  className={`p-3 border text-left transition-colors ${form.billing_mode === "firm_pays" ? "border-[var(--ll-ink)] bg-[var(--ll-mint)]" : "border-[var(--ll-border)] hover:bg-[var(--ll-mint)]"}`}
                >
                  <div className="text-sm font-medium">Firm pays</div>
                  <div className="text-[11px] text-[var(--ll-ink-soft)] mt-0.5">
                    This workspace rolls up onto your firm's bill. Consolidated billing.
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => set("billing_mode")("client_trial")}
                  data-testid="billing-client_trial"
                  className={`p-3 border text-left transition-colors ${form.billing_mode === "client_trial" ? "border-[var(--ll-ink)] bg-[var(--ll-mint)]" : "border-[var(--ll-border)] hover:bg-[var(--ll-mint)]"}`}
                >
                  <div className="text-sm font-medium">Client trial</div>
                  <div className="text-[11px] text-[var(--ll-ink-soft)] mt-0.5">
                    Client claims the workspace and picks their own plan.
                  </div>
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs mb-1">Starting plan</label>
                  <select
                    value={form.plan} onChange={set("plan")}
                    data-testid="client-plan"
                    className="w-full px-3 py-2 border text-sm bg-white" style={{ borderColor: "var(--ll-border)" }}
                  >
                    <option value="trial">Trial</option>
                    <option value="starter">Starter</option>
                    <option value="pro">Pro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs mb-1">Seats limit</label>
                  <input
                    type="number" min={1} max={50}
                    value={form.seats_limit} onChange={set("seats_limit")}
                    data-testid="client-seats"
                    className="w-full px-3 py-2 border text-sm font-mono-fin" style={{ borderColor: "var(--ll-border)" }}
                  />
                </div>
                <div>
                  <label className="block text-xs mb-1">Bank-account limit</label>
                  <input
                    type="number" min={1} max={20}
                    value={form.bank_account_limit} onChange={set("bank_account_limit")}
                    data-testid="client-accounts"
                    className="w-full px-3 py-2 border text-sm font-mono-fin" style={{ borderColor: "var(--ll-border)" }}
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox" checked={form.send_invite_email}
                  onChange={(e) => set("send_invite_email")(e.target.checked)}
                  data-testid="client-send-invite"
                />
                Email the client a magic-link invite so they can claim their portal
              </label>
            </div>
          </section>

          {/* Firm template picker — pre-seeds the new workspace with a saved
              chart-of-accounts preset. */}
          {templates.length > 0 && (
            <section>
              <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-2 flex items-center gap-1.5">
                <Layers size={11} /> Apply a template <span className="normal-case tracking-normal text-[var(--ll-ink-soft)]">(optional)</span>
              </div>
              <select
                value={form.template_id}
                onChange={set("template_id")}
                data-testid="client-template-id"
                className="w-full px-3 py-2 border text-sm bg-white"
                style={{ borderColor: "var(--ll-border)" }}
              >
                <option value="">No template — start blank</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.categories?.length || 0} categories){t.is_default ? " · default" : ""}
                  </option>
                ))}
              </select>
              <div className="text-[10px] text-[var(--ll-ink-muted)] mt-1">
                Preset categories get copied to the new workspace's chart of accounts.
              </div>
            </section>
          )}

          {err && (
            <div className="p-3 border bg-[#FEF2F2] border-[#FCA5A5] text-[#991B1B] text-sm" data-testid="add-client-error">
              {err}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t" style={{ borderColor: "var(--ll-border)" }}>
            <button
              type="button" onClick={close} disabled={saving}
              data-testid="add-client-cancel"
              className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)] disabled:opacity-50"
              style={{ borderColor: "var(--ll-border)" }}
            >
              Cancel
            </button>
            <button
              type="submit" disabled={saving}
              data-testid="add-client-submit"
              className="text-sm px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#0b5d9e] flex items-center gap-1.5 disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
              {saving ? "Creating…" : "Create client workspace"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
